import { useState, useMemo } from "react";
import { useAddNewNoteMutation } from "./notesApiSlice";
import { useNavigate } from "react-router-dom";

export default function NewNoteForm() {
  const navigate = useNavigate();
  const [addNewNote, { isLoading, isError, error }] = useAddNewNoteMutation();

  // Form state
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [text, setText] = useState("");
  const [description, setDescription] = useState("");
  const [completed, setCompleted] = useState(false);
  const [status, setStatus] = useState("open");


    // --- Validation (same rules as Edit) ---
    const validTitle = title.trim().length >= 3 && title.trim().length <= 80;
    const validShort = shortTitle.trim().length >= 3 && shortTitle.trim().length <= 60;
    const validDesc = description.trim().length > 0;
    const validText = text.trim().length > 0;


  const canSave =
    validTitle && validShort && validDesc && !isLoading;

  const onSaveNoteClicked = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    try {
      await addNewNote({
        title,
        shortTitle,
        text,
        description,
        completed,
        status
      }).unwrap();
      navigate("/dash/notes");
    } catch {
      /* handled by error banner */
    }
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6"
    >
      {/* Error banner */}
      {isError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
          {error?.data?.message || "Something went wrong."}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title <span className="text-gray-400 font-normal">[3–80 chars]</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validTitle ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            placeholder="Main Note Title"
          />
          {!validTitle && (
            <p className="mt-1 text-xs text-amber-600">Must be 3–80 characters.</p>
          )}
        </div>

        {/* Short title */}
        <div>
          <label htmlFor="shorttitle" className="block text-sm font-medium text-gray-700">
            Short Title <span className="text-gray-400 font-normal">[3–60 chars]</span>
          </label>
          <input
            id="shorttitle"
            type="text"
            value={shortTitle}
            onChange={(e) => setShortTitle(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validShort ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            placeholder="Short name"
          />
          {!validShort && (
            <p className="mt-1 text-xs text-amber-600">Must be 3–60 characters.</p>
          )}
        </div>

        {/* Text */}
        <div className="md:col-span-2">
          <label htmlFor="text" className="block text-sm font-medium text-gray-700">
            Text
          </label>
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validText ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            rows={3}
            placeholder="Note text..."
          />
          {!validText && (
            <p className="mt-1 text-xs text-amber-600">Text is required.</p>
          )}
        </div>
        
        {/* Description */}
        <div className="md:col-span-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validDesc ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            rows={3}
            placeholder="Note description..."
          />
          {!validDesc && (
            <p className="mt-1 text-xs text-amber-600">Description is required.</p>
          )}
        </div>


        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          type="submit"
          onClick={onSaveNoteClicked}
          disabled={!canSave}
          className={`rounded-xl px-4 py-2 text-sm text-white ${
            canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Save
        </button>
      </div>
    </form>
  );
}
